import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Banknote,
  CheckCircle2,
  Loader2,
  XCircle,
  RefreshCw,
  Filter,
  Landmark,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { TopBanner } from '@/components/TopBanner';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentsApi } from '@/api/payments.api';
import { hgcashApi } from '@/api/hgcash.api';
import { formatCurrency } from '@/lib/utils';

export const Route = createFileRoute('/_authenticated/retiros')({
  component: RetirosPage,
});

interface Payment {
  id: string;
  clientId: string;
  clientName?: string;
  type: 'deposit' | 'withdrawal';
  amount: string;
  currency: string;
  status: 'pending' | 'approved' | 'rejected';
  method?: string;
  bankAccountId?: string;
  notes?: string | null;
  externalTxId?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Saca destino y titular del notes del retiro ("CBU/Alias: X · Titular: Y"). */
function parseWithdrawNotes(notes?: string | null): { destino: string; titular: string } {
  const destino = notes?.match(/CBU\/Alias:\s*([^·\n]+)/)?.[1]?.trim() ?? '';
  const titular = notes?.match(/Titular:\s*([^|\n]+)/)?.[1]?.trim() ?? '';
  return { destino: destino === '-' ? '' : destino, titular: titular === '-' ? '' : titular };
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendiente',
  approved: 'Aprobado',
  rejected: 'Rechazado',
};

const STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  pending: 'secondary',
  approved: 'default',
  rejected: 'destructive',
};

function RetirosPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [payoutPayment, setPayoutPayment] = useState<Payment | null>(null);
  const [payoutDestino, setPayoutDestino] = useState('');
  const [payoutTipo, setPayoutTipo] = useState<'cbu' | 'cvu'>('cbu');
  const [payoutTitular, setPayoutTitular] = useState('');

  const { data: payments = [], isLoading } = useQuery({
    queryKey: ['payments', 'withdrawal', statusFilter],
    queryFn: async () => {
      const res = await paymentsApi.list({ type: 'withdrawal', status: statusFilter === 'all' ? undefined : statusFilter });
      // el backend devuelve el array directo (kind→type via paymentsApi); soporta ambos shapes
      const rows = Array.isArray(res) ? res : (res?.data ?? []);
      return rows as Payment[];
    },
  });

  const { data: hgConfig } = useQuery({
    queryKey: ['hgcash', 'config'],
    queryFn: hgcashApi.config,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const payoutMutation = useMutation({
    mutationFn: (p: { paymentId: string; toCBU?: string; toCVU?: string; toName?: string }) =>
      hgcashApi.payout(p),
    onSuccess: (res) => {
      toast.success(`Transferencia enviada a HG Cash (${res.status ?? 'PENDING'}) — se avisa al jugador cuando acredite`);
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      setPayoutPayment(null);
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      toast.error(err?.response?.data?.message || 'HG Cash rechazó la transferencia');
    },
  });

  const openPayout = (p: Payment) => {
    const parsed = parseWithdrawNotes(p.notes);
    setPayoutDestino(parsed.destino.replace(/\D/g, '').length === 22 ? parsed.destino.replace(/\D/g, '') : parsed.destino);
    setPayoutTitular(parsed.titular);
    setPayoutTipo('cbu');
    setPayoutPayment(p);
  };

  const submitPayout = () => {
    if (!payoutPayment) return;
    const destino = payoutDestino.replace(/\D/g, '');
    if (!/^\d{22}$/.test(destino)) {
      toast.error('El destino debe ser un CBU/CVU de 22 dígitos (si el jugador dio un alias, resolvelo primero)');
      return;
    }
    payoutMutation.mutate({
      paymentId: payoutPayment.id,
      ...(payoutTipo === 'cbu' ? { toCBU: destino } : { toCVU: destino }),
      ...(payoutTitular ? { toName: payoutTitular } : {}),
    });
  };

  const approveMutation = useMutation({
    mutationFn: paymentsApi.approve,
    onSuccess: () => {
      toast.success('Retiro aprobado');
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      setSelectedPayment(null);
      setActionType(null);
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Error al aprobar');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (id: string) => paymentsApi.reject(id, 'Rechazado por operador'),
    onSuccess: () => {
      toast.success('Retiro rechazado');
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      setSelectedPayment(null);
      setActionType(null);
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Error al rechazar');
    },
  });

  const columns = useMemo<ColumnDef<Payment>[]>(
    () => [
      {
        accessorKey: 'clientName',
        header: 'Cliente',
        cell: ({ row }) => (
          <div className="font-medium">{row.original.clientName || '—'}</div>
        ),
      },
      {
        accessorKey: 'amount',
        header: 'Monto',
        cell: ({ row }) => (
          <div className="font-mono font-medium">
            {formatCurrency(row.original.amount, row.original.currency)}
          </div>
        ),
      },
      {
        accessorKey: 'method',
        header: 'Método',
        cell: ({ row }) => (
          <span className="text-sm capitalize">{row.original.method || '—'}</span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Estado',
        cell: ({ row }) => (
          <Badge variant={STATUS_VARIANTS[row.original.status] || 'outline'}>
            {STATUS_LABELS[row.original.status] || row.original.status}
          </Badge>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Solicitado',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {new Date(row.original.createdAt).toLocaleString('es-AR')}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const p = row.original;
          if (p.status === 'approved' && hgConfig?.enabled && !p.externalTxId) {
            return (
              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                onClick={() => openPayout(p)}
              >
                <Landmark className="h-3.5 w-3.5" />
                Pagar por HG Cash
              </Button>
            );
          }
          if (p.status === 'approved' && p.externalTxId) {
            return <span className="text-xs text-muted-foreground">💸 HG enviado</span>;
          }
          if (p.status !== 'pending') return null;
          return (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1 text-green-600 border-green-200 hover:bg-green-50"
                onClick={() => {
                  setSelectedPayment(p);
                  setActionType('approve');
                }}
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Aprobar
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1 text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => {
                  setSelectedPayment(p);
                  setActionType('reject');
                }}
              >
                <XCircle className="h-3.5 w-3.5" />
                Rechazar
              </Button>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hgConfig?.enabled]
  );

  const pendingCount = payments.filter((p) => p.status === 'pending').length;

  return (
    <>
      <TopBanner
        title="Retiros"
        subtitle={`Solicitudes de retiro de fichas (${pendingCount} pendientes)`}
      />
      <div className="flex-1 overflow-auto p-6">
        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-3 mb-6">
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-amber-100 p-2.5">
                <Banknote className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{pendingCount}</div>
                <div className="text-xs text-muted-foreground">Pendientes</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-green-100 p-2.5">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {payments.filter((p) => p.status === 'approved').length}
                </div>
                <div className="text-xs text-muted-foreground">Aprobados hoy</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-red-100 p-2.5">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {payments.filter((p) => p.status === 'rejected').length}
                </div>
                <div className="text-xs text-muted-foreground">Rechazados hoy</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-4 w-4 text-muted-foreground" />
          {(['all', 'pending', 'approved', 'rejected'] as const).map((s) => (
            <Button
              key={s}
              size="sm"
              variant={statusFilter === s ? 'default' : 'outline'}
              onClick={() => setStatusFilter(s)}
            >
              {s === 'all' ? 'Todos' : STATUS_LABELS[s]}
            </Button>
          ))}
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['payments'] })}
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            Actualizar
          </Button>
        </div>

        {/* Table */}
        <DataTable
          columns={columns}
          data={payments}
          isLoading={isLoading}
          searchPlaceholder="Buscar por cliente..."
          searchColumn="clientName"
        />

        {/* Empty state */}
        {!isLoading && payments.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <Banknote className="h-12 w-12 mb-4 opacity-30" />
            <p className="text-lg font-medium">No hay retiros</p>
            <p className="text-sm">Las solicitudes de retiro aparecerán acá.</p>
          </div>
        )}
      </div>

      {/* Approve/Reject Dialog */}
      <Dialog open={!!selectedPayment && !!actionType} onOpenChange={() => { setSelectedPayment(null); setActionType(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' ? 'Aprobar retiro' : 'Rechazar retiro'}
            </DialogTitle>
            <DialogDescription>
              {selectedPayment && (
                <>
                  Cliente: <strong>{selectedPayment.clientName}</strong>
                  <br />
                  Monto: <strong>{formatCurrency(selectedPayment.amount, selectedPayment.currency)}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSelectedPayment(null); setActionType(null); }}>
              Cancelar
            </Button>
            {actionType === 'approve' ? (
              <Button
                onClick={() => selectedPayment && approveMutation.mutate(selectedPayment.id)}
                disabled={approveMutation.isPending}
              >
                {approveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Confirmar aprobación
              </Button>
            ) : (
              <Button
                variant="destructive"
                onClick={() => selectedPayment && rejectMutation.mutate(selectedPayment.id)}
                disabled={rejectMutation.isPending}
              >
                {rejectMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Confirmar rechazo
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payout HG Cash: transfiere el retiro YA aprobado al CBU/CVU del jugador */}
      <Dialog open={!!payoutPayment} onOpenChange={(o) => !o && setPayoutPayment(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>💸 Pagar retiro por HG Cash</DialogTitle>
            <DialogDescription>
              {payoutPayment && (
                <>
                  {payoutPayment.clientName || 'Cliente'} ·{' '}
                  <strong>{formatCurrency(payoutPayment.amount, payoutPayment.currency)}</strong>
                  <br />
                  La transferencia sale de la cuenta HG Cash. El jugador recibe aviso en el chat al acreditarse.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              {(['cbu', 'cvu'] as const).map((t) => (
                <Button
                  key={t}
                  size="sm"
                  variant={payoutTipo === t ? 'default' : 'outline'}
                  onClick={() => setPayoutTipo(t)}
                >
                  {t.toUpperCase()}
                </Button>
              ))}
            </div>
            <div>
              <Input
                placeholder={`${payoutTipo.toUpperCase()} (22 dígitos)`}
                value={payoutDestino}
                onChange={(e) => setPayoutDestino(e.target.value)}
                className="font-mono"
              />
              {payoutDestino && !/^\d{22}$/.test(payoutDestino.replace(/\D/g, '')) && (
                <p className="mt-1 text-[11px] text-amber-500">
                  ⚠️ No son 22 dígitos — si el jugador pasó un alias, conseguí el CBU/CVU real.
                </p>
              )}
            </div>
            <Input
              placeholder="Titular (opcional)"
              value={payoutTitular}
              onChange={(e) => setPayoutTitular(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayoutPayment(null)}>
              Cancelar
            </Button>
            <Button onClick={submitPayout} disabled={payoutMutation.isPending}>
              {payoutMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Transferir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
